import type { FC, PropsWithChildren } from 'react';
import React, { createContext, useContext } from 'react';
import { useFragment } from 'react-relay';

import { fg } from '@confluence/feature-gating';

import type {
	AudioContentFragment_content$data,
	AudioContentFragment_content$key,
} from './__generated__/AudioContentFragment_content.graphql.ts';
import { AudioContentFragment } from './AudioContentFragment.ts';

const AudioContentContext = createContext<AudioContentFragment_content$data | undefined>(undefined);

type AudioContentProviderProps = PropsWithChildren<{
	contentId: string;
	contentFragmentRef?: AudioContentFragment_content$key | null;
}>;

const AudioContentFragmentProvider: FC<AudioContentProviderProps> = ({
	children,
	contentId,
	contentFragmentRef,
}) => {
	const content = useFragment<AudioContentFragment_content$key>(
		AudioContentFragment,
		contentFragmentRef ?? null,
	);
	const audioContent = content?.id === contentId ? content : undefined;

	return (
		<AudioContentContext.Provider value={audioContent}>{children}</AudioContentContext.Provider>
	);
};

export const AudioContentProvider: FC<AudioContentProviderProps> = ({
	children,
	contentId,
	contentFragmentRef,
}) => {
	if (!fg('confluence_vp_audio_frag_reader') || !contentFragmentRef) {
		return (
			<AudioContentContext.Provider value={undefined}>{children}</AudioContentContext.Provider>
		);
	}

	return (
		<AudioContentFragmentProvider contentId={contentId} contentFragmentRef={contentFragmentRef}>
			{children}
		</AudioContentFragmentProvider>
	);
};

export const useAudioContentFragmentData = (): AudioContentFragment_content$data | undefined =>
	useContext(AudioContentContext);
